import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/constants/queryKeys'
import { useSyncState } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { OrderModel } from '@/http/models'
import { ReturnRequest } from '@/db/repositories/orders'
import { PosHttpParams, PosModel } from '@/types'

import { unwrap } from '../unwrap'

export const useOrders = (search: string, shift = false) =>
  useQuery({
    queryKey: queryKeys.orders(search, shift),
    queryFn: () => unwrap(OrderModel.getList({ search, shift })),
    placeholderData: keepPreviousData,
  })

/** A sale or refund changes stock, so both refresh the product grid as well as the order list. */
const useInvalidateAfterSale = () => {
  const client = useQueryClient()
  return () => {
    client.invalidateQueries({ queryKey: ['orders'] })
    client.invalidateQueries({ queryKey: ['products'] })
  }
}

export const useCreateOrder = () => {
  const invalidate = useInvalidateAfterSale()
  return useMutation({
    mutationFn: (body: PosHttpParams.CreateOrder) => unwrap(OrderModel.create(body)),
    onSuccess: invalidate,
  })
}

export const useReturnItems = () => {
  const invalidate = useInvalidateAfterSale()
  return useMutation({
    mutationFn: (input: { id: string; request: ReturnRequest }) => unwrap(OrderModel.returnItems(input.id, input.request)),
    onSuccess: invalidate,
  })
}

export const useCancelOrder = () => {
  const invalidate = useInvalidateAfterSale()
  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) => unwrap(OrderModel.cancel(input.id, input.reason)),
    onSuccess: invalidate,
  })
}

export const useRefundOrder = () => {
  const invalidate = useInvalidateAfterSale()
  return useMutation({ mutationFn: (id: string) => unwrap(OrderModel.refund(id)), onSuccess: invalidate })
}

/** A device sale's sync state, re-read after every sync run; the demo's sales have none. */
export const useOrderSyncState = (order: PosModel.Order | null) => {
  const { lastSyncedAt } = useSyncState()
  const { data } = useQuery({
    queryKey: ['order-sync-state', order?._id, lastSyncedAt],
    queryFn: async () => {
      const row = await rows('orders').get(order!._id)
      return row ? (row.synced === 1 ? 'synced' : row.synced === -1 ? 'rejected' : 'pending') : null
    },
    enabled: !!order?.sync_state,
  })
  return data ?? order?.sync_state ?? null
}
